import React, { useState } from 'react';
import { useAIShortcuts, AIShortcut } from '@/hooks/useAIShortcuts';
import { PlusIcon, Trash2, Edit2, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

export const AIShortcutsSection = () => {
    const { shortcuts, isLoading, isSaving, addShortcut, updateShortcut, deleteShortcut } = useAIShortcuts();
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [newName, setNewName] = useState('');
    const [newPrompt, setNewPrompt] = useState('');

    const handleAdd = async () => {
        if (!newName || !newPrompt) {
            toast.error('Name and prompt are required');
            return;
        }
        await addShortcut({ name: newName, prompt: newPrompt });
        setNewName('');
        setNewPrompt('');
        setIsAdding(false);
        toast.success('Shortcut added');
    };

    const handleUpdate = async (s: AIShortcut) => {
        if (!newName || !newPrompt) {
            toast.error('Name and prompt are required');
            return;
        }
        await updateShortcut({ ...s, name: newName, prompt: newPrompt });
        setEditingId(null);
        setNewName('');
        setNewPrompt('');
        toast.success('Shortcut updated');
    };

    const startEdit = (s: AIShortcut) => {
        setEditingId(s.id);
        setNewName(s.name);
        setNewPrompt(s.prompt);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-gray-700">Custom AI Shortcuts</h3>
                {!isAdding && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <PlusIcon className="w-3 h-3" /> Add Shortcut
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shortcuts.map((s) => (
                    <div key={s.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm group relative hover:border-blue-200 transition-all">
                        {editingId === s.id ? (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="w-full text-sm font-bold border-gray-200 rounded-lg focus:ring-blue-500"
                                    placeholder="Shortcut Name"
                                    autoFocus
                                />
                                <textarea
                                    value={newPrompt}
                                    onChange={(e) => setNewPrompt(e.target.value)}
                                    className="w-full text-xs border-gray-200 rounded-lg focus:ring-blue-500 h-20"
                                    placeholder="AI Prompt Instruction"
                                />
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                                    <button onClick={() => handleUpdate(s)} className="p-1 text-green-600 hover:text-green-700"><Check className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-sm font-bold text-gray-900 bg-gray-50 px-2 py-0.5 rounded-md">{s.name}</span>
                                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => startEdit(s)} className="p-1 text-gray-400 hover:text-blue-600"><Edit2 className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => deleteShortcut(s.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2 italic">"{s.prompt}"</p>
                            </>
                        )}
                    </div>
                ))}

                {isAdding && (
                    <div className="bg-blue-50/50 border-2 border-dashed border-blue-200 rounded-xl p-4 transition-all">
                        <div className="space-y-3">
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="w-full text-sm font-bold border-blue-100 rounded-lg focus:ring-blue-500 placeholder:text-blue-300"
                                placeholder="New Shortcut Name"
                                autoFocus
                            />
                            <textarea
                                value={newPrompt}
                                onChange={(e) => setNewPrompt(e.target.value)}
                                className="w-full text-xs border-blue-100 rounded-lg focus:ring-blue-500 h-20 placeholder:text-blue-300"
                                placeholder="Describe what the AI should do (e.g., 'Rewrite this in a more playful tone')"
                            />
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setIsAdding(false)} className="px-3 py-1 text-xs font-medium text-gray-500 hover:text-gray-700">Cancel</button>
                                <button onClick={handleAdd} className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-sm">Add Shortcut</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIShortcutsSection;
